'use client';

import { Button } from '@balder/ui/components/button';
import { cn } from '@balder/ui/lib/cn';
import { BalderWordmark } from './balder-wordmark';
import {
  Check,
  CircleAlert,
  Copy,
  FileX2,
  Link2Off,
  Lock,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { buildSupportMailto, SUPPORT_EMAIL } from './support-mailto';

/**
 * The one failure-state component. Every error, 404, 403, dead-link and stalled-wait
 * surface in the dashboard is this stack - mark, headline, sentence, one button, one
 * link, footnote - with rows switched on or off.
 *
 * Design: docs/plans/2026-07-28-error-pages-design-brief.md (Claude Design "Error Surfaces v2").
 * Tier 1 (the app itself failed) deliberately does NOT use this component: global-error.tsx
 * renders above the theme and providers, so it owns no dependencies and inlines everything.
 */

export type ErrorStateTier = 'inline' | 'route';
export type ErrorStateKind = 'error' | 'notFound' | 'noAccess' | 'linkDead' | 'pending';

type Action = { label: string; href?: string; onClick?: () => void };

export interface ErrorStateProps {
  /** `inline` stays inside the panel's bounds and never fills the viewport. */
  tier: ErrorStateTier;
  /** Chooses the shape: recovery, navigation, identity, dead end, waiting. */
  kind: ErrorStateKind;
  title: ReactNode;
  /** One sentence. Two is a smell. */
  description?: ReactNode;
  /** Overrides the per-kind default glyph. */
  icon?: LucideIcon;
  /** Pins the wordmark to the page's top-left. True only where there is no app shell. */
  brandMark?: boolean;
  /** At most one, always filled. Absent entirely on dead share links. */
  primaryAction?: Action;
  /** A text link under the button, not a second button. Never at tier `inline`. */
  secondaryAction?: Action;
  /** The 404 onward list. One sentence on desktop, tappable rows on mobile. */
  links?: { label: string; href: string }[];
  /** Present => renders the notified line and the copyable code together. They never ship apart. */
  referenceId?: string;
  /**
   * Who you are signed in as. `card` puts it in the column (403, where "which account
   * am I" IS the content); `footnote` is the quieter one-liner (org 404, where identity
   * is a hypothesis rather than the answer). Sign out lives inside the card, never in
   * the action stack.
   */
  identity?: {
    /** Undefined while Clerk is still resolving - the slot renders a skeleton at the
     *  same height rather than appearing late and shoving the block around. */
    email?: string;
    role?: string;
    variant?: 'card' | 'footnote';
    onSignOut?: () => void;
  };
  /**
   * Who to ask. Always a text link in the footnote block.
   *
   * There is deliberately no `technicalDetail` prop. The tRPC errorFormatter spreads
   * `...shape`, so `error.message` reaching the client is the RAW server message - the
   * `userMessage` field exists precisely because `message` is not safe to show. Rendering
   * it would surface Postgres constraint values, query inputs and internal strings to an
   * operations audience that cannot act on any of it. The full error and componentStack
   * are already in PostHog under the same `reference_id`.
   */
  support?: 'balder' | 'orgAdmin' | false;
  /** The quiet mono line at the very bottom, e.g. "Error 404". */
  statusCode?: string;
  /** Bespoke footnote copy, for the surfaces the props above don't describe. */
  footnote?: ReactNode;
  className?: string;
}

const KIND_ICON: Record<ErrorStateKind, LucideIcon> = {
  error: CircleAlert,
  notFound: FileX2,
  noAccess: Lock,
  linkDead: Link2Off,
  pending: CircleAlert,
};

// The column width is what makes the copy break where the design breaks it.
const KIND_WIDTH: Record<ErrorStateKind, string> = {
  error: 'max-w-[340px]',
  notFound: 'max-w-[360px]',
  noAccess: 'max-w-[360px]',
  linkDead: 'max-w-[400px]',
  pending: 'max-w-[360px]',
};

export const ErrorState = ({
  tier,
  kind,
  title,
  description,
  icon,
  brandMark = false,
  primaryAction,
  secondaryAction,
  links,
  referenceId,
  identity,
  support = false,
  statusCode,
  footnote,
  className,
}: ErrorStateProps) => {
  if (tier === 'inline') {
    return (
      <InlineErrorState
        kind={kind}
        title={title}
        description={description}
        icon={icon}
        primaryAction={primaryAction}
        className={className}
      />
    );
  }

  const Icon = icon ?? KIND_ICON[kind];
  const identityAsCard = identity && identity.variant !== 'footnote';
  const hasFootnote =
    referenceId ||
    links?.length ||
    support ||
    identity?.variant === 'footnote' ||
    statusCode ||
    footnote;

  return (
    <div
      className={cn(
        'flex w-full flex-1 items-center justify-center px-6 pt-12 pb-20 sm:px-12 sm:pb-[120px]',
        className,
      )}
    >
      {brandMark && <BrandMark />}
      <div className={cn('flex w-full flex-col items-center text-center', KIND_WIDTH[kind])}>
        {kind === 'pending' ? (
          <div
            role="status"
            aria-label="Working"
            className="size-[22px] animate-spin rounded-full border-2 border-border border-t-muted-foreground"
          />
        ) : (
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground">
            <Icon strokeWidth={1.7} className="size-[19px]" />
          </div>
        )}

        <h1
          className={cn(
            'text-[17px] leading-[1.4] font-semibold tracking-[-0.005em]',
            kind === 'linkDead' && 'text-lg tracking-[-0.01em] sm:text-[19px]',
            kind === 'notFound' || kind === 'linkDead' ? 'mt-5' : 'mt-[18px]',
          )}
        >
          {title}
        </h1>

        {description && (
          <p
            className={cn(
              'mt-[7px] text-[13px] leading-[1.65] text-pretty text-muted-foreground',
              kind === 'linkDead' && 'mt-2 leading-[1.7]',
            )}
          >
            {description}
          </p>
        )}

        {identityAsCard && <IdentityCard identity={identity} />}

        {primaryAction && (
          <ActionButton
            action={primaryAction}
            // Nothing has failed on a `pending` screen, so its action stays quiet.
            outline={kind === 'pending'}
            className={cn(identityAsCard ? 'mt-5' : 'mt-6 sm:mt-[22px]')}
          />
        )}

        {secondaryAction && <SecondaryLink action={secondaryAction} />}

        {hasFootnote && (
          <div className="mt-[34px] w-full text-xs leading-[1.9] text-pretty text-muted-foreground">
            {footnote}
            {links?.length ? <OnwardLinks links={links} /> : null}
            {identity?.variant === 'footnote' && <IdentityFootnote identity={identity} />}
            {referenceId && <ReferenceLine referenceId={referenceId} />}
            {support && <SupportLine support={support} referenceId={referenceId} />}
            {statusCode && (
              <div className="mt-[18px] font-mono text-[11px] opacity-70 sm:mt-0 sm:inline sm:text-[11px]">
                {statusCode}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Tier 3 shorthand for the common case: one panel's query failed, retry in place.
 * Pass the `resetErrorBoundary` from react-error-boundary as `onRetry`.
 */
export const InlinePanelError = ({
  title,
  description,
  onRetry,
  icon,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  onRetry?: () => void;
  icon?: LucideIcon;
  className?: string;
}) => (
  <ErrorState
    tier="inline"
    kind="error"
    title={title}
    description={description}
    icon={icon}
    primaryAction={onRetry ? { label: 'Try again', onClick: onRetry } : undefined}
    className={className}
  />
);

/* -------------------------------------------------------------------------- */
/* Tier 3 - a panel failed. A few words and a button, inside the panel's bounds.  */
/* -------------------------------------------------------------------------- */

const InlineErrorState = ({
  kind,
  title,
  description,
  icon,
  primaryAction,
  className,
}: Pick<
  ErrorStateProps,
  'kind' | 'title' | 'description' | 'icon' | 'primaryAction' | 'className'
>) => {
  const Icon = icon ?? KIND_ICON[kind];

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col items-center justify-center px-5 py-10 text-center',
        className,
      )}
    >
      <Icon strokeWidth={1.7} className="size-5 text-muted-foreground" />
      <div className="mt-3.5 text-sm leading-[1.4] font-medium">{title}</div>
      {description && (
        <div className="mt-[5px] text-[13px] leading-[1.6] text-muted-foreground">
          {description}
        </div>
      )}
      {primaryAction && (
        <Button
          variant="outline"
          onClick={primaryAction.onClick}
          className={cn(
            'h-11 gap-[7px] px-4 text-[13px] sm:h-10',
            description ? 'mt-4' : 'mt-[18px]',
          )}
        >
          <RefreshCw strokeWidth={1.7} className="size-3.5" />
          {primaryAction.label}
        </Button>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Parts                                                                       */
/* -------------------------------------------------------------------------- */

// Inline SVG on `currentColor`, so it paints immediately and follows the foreground token
// in either theme. A CSS mask pointing at the logo file made it pop in a beat late.
const BrandMark = () => (
  <BalderWordmark className="absolute top-7 left-6 h-[13px] w-auto text-foreground sm:top-9 sm:left-10 sm:h-3.5" />
);

// 48px full-width on mobile, 44px auto on desktop - taller than any @balder/ui size,
// so the height comes from className while the variant keeps focus rings and disabled state.
const ActionButton = ({
  action,
  outline = false,
  className,
}: {
  action: Action;
  outline?: boolean;
  className?: string;
}) => (
  <Button
    asChild={Boolean(action.href)}
    variant={outline ? 'outline' : 'default'}
    onClick={action.href ? undefined : action.onClick}
    className={cn('h-12 w-full px-5 text-[15px] sm:h-11 sm:w-auto sm:text-sm', className)}
  >
    {action.href ? (
      <a href={action.href}>{action.label}</a>
    ) : (
      <>
        {action.label === 'Try again' && <RefreshCw strokeWidth={1.7} className="size-[15px]" />}
        {action.label}
      </>
    )}
  </Button>
);

// Visually a link, but padded to a 48px mobile / 44px desktop hit box for coarse pointers.
const SecondaryLink = ({ action }: { action: Action }) => (
  <Button
    asChild={Boolean(action.href)}
    variant="link"
    onClick={action.href ? undefined : action.onClick}
    className="mt-[3px] h-auto px-[18px] py-[15px] text-sm no-underline hover:no-underline sm:mt-px sm:py-[13px] sm:text-[13px]"
  >
    {action.href ? <a href={action.href}>{action.label}</a> : action.label}
  </Button>
);

type Identity = NonNullable<ErrorStateProps['identity']>;

// The card keeps its own height regardless, so only the text inside needs a placeholder.
const IdentityCard = ({ identity }: { identity: Identity }) => (
  <div className="mt-5 flex w-full items-center gap-[11px] rounded-md border py-[9px] pr-[9px] pl-3 text-left">
    <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
      {identity.email ? initials(identity.email) : null}
    </span>
    <span className="min-w-0 flex-1">
      {identity.email ? (
        <span className="block truncate text-[13px] font-medium">{identity.email}</span>
      ) : (
        <span className="inline-block h-3 w-44 max-w-full animate-pulse rounded bg-muted align-middle text-[13px] leading-normal" />
      )}
      {identity.role && (
        <span className="block truncate text-[11px] text-muted-foreground">{identity.role}</span>
      )}
    </span>
    {identity.onSignOut && (
      <Button
        variant="outline"
        onClick={identity.onSignOut}
        disabled={!identity.email}
        className="h-10 shrink-0 px-[13px] text-xs"
      >
        Sign out
      </Button>
    )}
  </div>
);

const IdentityFootnote = ({ identity }: { identity: Identity }) =>
  identity.email ? (
    <div>
      You&apos;re signed in as <span className="text-foreground">{identity.email}</span>.
      <br />
      You might need to{' '}
      {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- every link on these
          surfaces is a hard navigation on purpose: client-side routing is unreliable inside
          error/not-found boundaries, and a full load is what escapes the broken state. */}
      <a href="/sign-in" className="text-foreground hover:underline">
        sign in with a different email
      </a>
      .
    </div>
  ) : (
    // Same two-line structure as the real copy - inline-block bars inside the parent's
    // 1.9 line-height, so each line box is identical whether it holds text or a bar and
    // nothing moves when Clerk resolves a beat later.
    <div aria-hidden>
      <span className="inline-block h-2.5 w-56 max-w-full animate-pulse rounded bg-muted align-middle" />
      <br />
      <span className="inline-block h-2.5 w-48 max-w-full animate-pulse rounded bg-muted align-middle" />
    </div>
  );

const OnwardLinks = ({ links }: { links: { label: string; href: string }[] }) => (
  <>
    {/* Mobile: 50px tappable rows. */}
    <div className="w-full text-left sm:hidden">
      <div className="pb-1.5 text-[10px] font-medium tracking-[0.1em] text-muted-foreground uppercase">
        Or go to
      </div>
      {links.map((link) => (
        <a
          key={link.label}
          href={link.href}
          className="flex h-[50px] items-center justify-between border-t text-[15px] text-foreground first-letter:uppercase last:border-b"
        >
          {link.label}
          <span aria-hidden className="text-muted-foreground">
            ›
          </span>
        </a>
      ))}
    </div>
    {/* Desktop: one quiet sentence. */}
    <div className="hidden sm:block">
      Or go to{' '}
      {links.map((link, i) => (
        <span key={link.label}>
          <a href={link.href} className="text-foreground hover:underline">
            {link.label}
          </a>
          {i < links.length - 2 ? ', ' : i === links.length - 2 ? ' or ' : '.'}
        </span>
      ))}
    </div>
  </>
);

/**
 * A 12px glyph carrying a 40px hit box, negative-margined so it does not disturb the
 * 12px footnote line it sits in. Used for both quotable strings in that block - the
 * reference and the support address - so they read as one pattern.
 */
const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`}
      onClick={() => {
        // Not the shared useCopyToClipboard hook: that reports via toast, and the design
        // asks for inline confirmation - which is also the only thing that can work on the
        // fatal screen, where no Toaster is mounted. writeText rejects under a restrictive
        // permissions policy, so failure must not claim success; the value is rendered as
        // visible text either way, so it stays selectable by hand.
        void (async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
          } catch {
            /* clipboard unavailable - the value is still on screen to select */
          }
        })();
      }}
      className="-my-3.5 -mr-3.5 -ml-[9px] inline-flex size-10 items-center justify-center align-[-2px] transition-colors hover:text-foreground"
    >
      {copied ? (
        <Check strokeWidth={1.7} className="size-3" />
      ) : (
        <Copy strokeWidth={1.7} className="size-3" />
      )}
    </button>
  );
};

const ReferenceLine = ({ referenceId }: { referenceId: string }) => (
  <div>
    We&apos;ve been notified. Reference{' '}
    <span className="font-mono text-xs tracking-[0.02em] text-foreground">{referenceId}</span>
    <CopyButton value={referenceId} label="Reference" />
  </div>
);

// Support and the technical-detail toggle share one 12px line, separated by a middot -
// the detail is never body copy, only a link that opens a labelled, copyable block.
const SupportLine = ({
  support,
  referenceId,
}: {
  support: 'balder' | 'orgAdmin';
  referenceId?: string;
}) => {
  const [pageUrl, setPageUrl] = useState('');
  useEffect(() => setPageUrl(window.location.href), []);

  const mailto = buildSupportMailto({ referenceId, pageUrl });

  return (
    <div>
      {support === 'orgAdmin' ? (
        <>
          Access is managed by your organisation, so we can&apos;t grant it for you. If this looks
          like a mistake in Balder Cloud, email{' '}
          <a href={mailto} className="text-foreground hover:underline">
            {SUPPORT_EMAIL}
          </a>
          <CopyButton value={SUPPORT_EMAIL} label="Support email" />
        </>
      ) : (
        <>
          Still stuck? Email{' '}
          <a href={mailto} className="text-foreground hover:underline">
            {SUPPORT_EMAIL}
          </a>
          <CopyButton value={SUPPORT_EMAIL} label="Support email" />
        </>
      )}
    </div>
  );
};

const initials = (email: string) => email.slice(0, 2).toUpperCase();
